import React from 'react';
import { Box, Grid2 } from '@mui/material';

import { Formik } from 'formik';
import * as Yup from 'yup';

import CustomDialog from '../lib/ui-components/Dialog/CustomDialog';
import CustomDialogContent from '../lib/ui-components/Dialog/CustomDialogContent';
import CustomTypography from '../lib/ui-components/Typography/CustomTypography';
import CustomSelect from '../lib/ui-components/Select/CustomSelect';
import CustomTextField from '../lib/ui-components/TextField/CustomTextField';
import CustomDialogActions from '../lib/ui-components/Dialog/CustomDialogActions';
import CustomButton from '../lib/ui-components/Button/CustomButton';
import {
  type GeneralType,
  type CancelConsultMetadataResponse,
} from './statusRequestService';

export interface CancelReasonFacilitySubmitParams {
  cancelReasonId: number;
  technicalDifficultiesId: number;
  otherReason: string;
}

export interface CancelReasonFacilityParams {
  cancelReasonId: number;
  technicalDifficultiesId: number;
  patientAdmittedId?: number;
  patientDischargedId?: number;
  noLongerRequiredId?: number;
  otherReason: string;
}

const cancelConsultinitialValues: CancelReasonFacilityParams = {
  cancelReasonId: 0,
  technicalDifficultiesId: 0,
  otherReason: '',
  patientAdmittedId: 0,
  noLongerRequiredId: 0,
};

export interface CancelConsultConfirmationPopupProps {
  openCancelConsultPopup: boolean;
  onCloseCancelConsultPopup: () => void;
  ConsultCancelMetaData: CancelConsultMetadataResponse | undefined;
  onSubmitCancelConsult: (data: CancelReasonFacilityParams) => void;
  initialValues?: CancelReasonFacilityParams | null;
  portal?: string;
}

const CancelConsultModel: React.FC<CancelConsultConfirmationPopupProps> = ({
  openCancelConsultPopup,
  onCloseCancelConsultPopup,
  ConsultCancelMetaData,
  onSubmitCancelConsult,
  initialValues,
  portal,
}) => {
  const [cancelConsultValues, setCancelConsultValues] =
    React.useState<CancelReasonFacilityParams>(cancelConsultinitialValues);

  React.useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0) {
      setCancelConsultValues(initialValues);
    }
  }, []);

  const getReasonName = (list: GeneralType[] = [], id?: number) => {
    return list.find((x) => x.id === id)?.name?.toLowerCase() || '';
  };

  const technicalId = ConsultCancelMetaData?.cancelReasonList?.find(
    (x) => x.name.toLowerCase() === 'technical difficulties'
  )?.id;

  const patientAdmittedReasonId = ConsultCancelMetaData?.cancelReasonList?.find(
    (x) => x.name.toLowerCase() === 'patient admitted'
  )?.id;

  const consultNoLongerNeeded = ConsultCancelMetaData?.cancelReasonList?.find(
    (x) => x.name.toLowerCase() === 'consult no longer needed'
  )?.id;

  const patientDischarged = ConsultCancelMetaData?.cancelReasonList?.find(
    (x) => x.name.toLowerCase() === 'patient discharged'
  )?.id;

  const otherId = ConsultCancelMetaData?.cancelReasonList?.find(
    (x) => x.name.toLowerCase() === 'other'
  )?.id;

  const validationReasonSchema = Yup.object({
    cancelReasonId: Yup.number()
      .min(1, 'Cancel reason is required')
      .required('Cancel reason is required'),

    technicalDifficultiesId: Yup.number().when(
      'cancelReasonId',
      ([cancelReasonId], schema) =>
        cancelReasonId === Number(technicalId)
          ? schema
              .min(1, 'Technical Difficulties is required')
              .required('Technical Difficulties is required')
          : schema.notRequired()
    ),
    patientAdmittedId: Yup.number().when(
      'cancelReasonId',
      ([cancelReasonId], schema) =>
        (portal?.toLowerCase() === 'facility' ||
          portal?.toLowerCase() === 'operations') &&
        cancelReasonId === Number(patientAdmittedReasonId)
          ? schema
              .min(1, 'Patient Admitted is required')
              .required('Patient Admitted is required')
          : schema.notRequired()
    ),
    noLongerRequiredId: Yup.number().when(
      'cancelReasonId',
      ([cancelReasonId], schema) =>
        (portal?.toLowerCase() === 'facility' ||
          portal?.toLowerCase() === 'operations') &&
        cancelReasonId === Number(consultNoLongerNeeded)
          ? schema.min(1, 'Reason is required').required('Reason is required')
          : schema.notRequired()
    ),
    patientDischargedId: Yup.number().when(
      'cancelReasonId',
      ([cancelReasonId], schema) =>
        (portal?.toLowerCase() === 'clinical' ||
          portal?.toLowerCase() === 'operations') &&
        cancelReasonId === Number(patientDischarged)
          ? schema.min(1, 'Reason is required').required('Reason is required')
          : schema.notRequired()
    ),
    otherReason: Yup.string().when(
      'cancelReasonId',
      ([cancelReasonId], schema) =>
        cancelReasonId === Number(otherId)
          ? schema.trim().required('Other Reason is required')
          : schema.notRequired()
    ),
  });

  const handleSubmitConsultCancelReason = async (
    cancelReasonData: typeof cancelConsultinitialValues
  ) => {
    onSubmitCancelConsult(cancelReasonData);
  };

  return (
    <Box>
      <Formik
        initialValues={cancelConsultValues}
        validateOnBlur
        enableReinitialize
        validationSchema={validationReasonSchema}
        onSubmit={(values) => {
          handleSubmitConsultCancelReason(values);
        }}
      >
        {({
          setFieldValue,
          values,
          handleSubmit,
          resetForm,
          errors,
          touched,
          validateField,
        }) => {
          const selectedCancelReason =
            ConsultCancelMetaData?.cancelReasonList?.find(
              (x) => x.id === values.cancelReasonId
            );

          const selectedReason = getReasonName(
            ConsultCancelMetaData?.cancelReasonList,
            values.cancelReasonId
          );

          const isTechnicalDifficulty =
            selectedCancelReason?.name.toLowerCase() ===
            'technical difficulties';

          const isOthers = selectedCancelReason?.name.toLowerCase() === 'other';

          const isPatientAdmitted = selectedReason === 'patient admitted';

          const isPatientDischarged =
            selectedReason.includes('patient discharged');

          const isNoLongerRequired =
            selectedReason.includes('no longer needed');

          return (
            <CustomDialog
              id="cancel-consult-dialog"
              open={openCancelConsultPopup}
              onClose={() => {
                onCloseCancelConsultPopup();
                resetForm();
              }}
              disableEscapeKeyDown={true}
            >
              <CustomDialogContent className="dialog-width">
                <Box className="pb-6">
                  <CustomTypography
                    id="cancel-consult-title-popup"
                    variant="subtitle1"
                  >
                    Cancel Consult
                  </CustomTypography>
                </Box>
                <Box>
                  <CustomTypography id="onhold-title-popup" variant="body3">
                    Are you sure you want to Cancel this consult?
                  </CustomTypography>
                  {portal?.toLowerCase() !== 'operations' && (
                    <CustomTypography id="onhold-title-popup" variant="body3">
                      This action cannot be undone.
                    </CustomTypography>
                  )}
                </Box>

                <Box className="mt-16">
                  <CustomSelect
                    id="select2"
                    placeholder="Select Cancel Reason"
                    options={ConsultCancelMetaData?.cancelReasonList || []}
                    selectedValue={values.cancelReasonId}
                    onChange={(e) => {
                      const value = Number(e);
                      setFieldValue('cancelReasonId', value, false);

                      setTimeout(() => {
                        validateField('cancelReasonId');
                      }, 0);
                      if (
                        isTechnicalDifficulty ||
                        isOthers ||
                        isPatientAdmitted ||
                        consultNoLongerNeeded ||
                        patientDischarged
                      ) {
                        setFieldValue('technicalDifficultiesId', 0);
                        setFieldValue('otherReason', '');
                        setFieldValue('patientAdmittedId', 0);
                        setFieldValue('noLongerRequiredId', 0);
                        setFieldValue('patientDischargedId', 0);
                      }
                    }}
                    size="small"
                    fullWidth
                  />
                  {errors?.cancelReasonId && touched?.cancelReasonId && (
                    <CustomTypography
                      id="validate-cancelReason"
                      color="error"
                      variant="body3"
                      className="pt-8"
                    >
                      {errors.cancelReasonId}
                    </CustomTypography>
                  )}
                </Box>
                {isTechnicalDifficulty && (
                  <Box className="mt-16">
                    <CustomSelect
                      id="cancel-consult-technical-difficulties"
                      placeholder="Select Technical Difficulty"
                      options={ConsultCancelMetaData?.cancelTechIssueList || []}
                      selectedValue={values.technicalDifficultiesId || 0}
                      onChange={(e) => {
                        setFieldValue('technicalDifficultiesId', e);
                      }}
                      size="small"
                      displayEmpty={true}
                      fullWidth
                    />
                    {errors?.technicalDifficultiesId &&
                      touched?.technicalDifficultiesId && (
                        <CustomTypography
                          id="validate-subCancelReason"
                          color="error"
                          variant="body3"
                          className="pt-8"
                        >
                          {errors.technicalDifficultiesId}
                        </CustomTypography>
                      )}
                  </Box>
                )}
                {isPatientAdmitted && (
                  <Box className="mt-16">
                    <CustomSelect
                      id="cancel-consult-patient-admitted"
                      placeholder="Select Admission Type"
                      options={ConsultCancelMetaData?.patientAdmittedList || []}
                      selectedValue={values.patientAdmittedId || 0}
                      onChange={(e) => {
                        setFieldValue('patientAdmittedId', e);
                      }}
                      size="small"
                      displayEmpty={true}
                      fullWidth
                    />
                    {errors?.patientAdmittedId &&
                      touched?.patientAdmittedId && (
                        <CustomTypography
                          id="validate-subCancelReason"
                          color="error"
                          variant="body3"
                          className="pt-8"
                        >
                          {errors.patientAdmittedId}
                        </CustomTypography>
                      )}
                  </Box>
                )}

                {portal?.toLowerCase() !== 'facility' &&
                  isPatientDischarged && (
                    <Box className="mt-16">
                      <CustomSelect
                        id="cancel-consult-patient-discharge"
                        placeholder="Select Discharge Type"
                        options={
                          ConsultCancelMetaData?.patientDischargedList || []
                        }
                        selectedValue={values.patientDischargedId}
                        onChange={(e) =>
                          setFieldValue('patientDischargedId', e)
                        }
                        size="small"
                        displayEmpty={true}
                        fullWidth
                      />
                      {errors?.patientDischargedId &&
                        touched?.patientDischargedId && (
                          <CustomTypography
                            id="validate-subCancelReason"
                            color="error"
                            variant="body3"
                            className="pt-8"
                          >
                            {errors.patientDischargedId}
                          </CustomTypography>
                        )}
                    </Box>
                  )}

                {isNoLongerRequired && (
                  <Box className="mt-16">
                    <CustomSelect
                      id="cancel-consult-patient-noLongerRequiredList"
                      placeholder="Select Reason"
                      options={
                        ConsultCancelMetaData?.noLongerRequiredList || []
                      }
                      selectedValue={values.noLongerRequiredId || 0}
                      onChange={(e) => setFieldValue('noLongerRequiredId', e)}
                      size="small"
                      displayEmpty={true}
                      fullWidth
                    />
                    {errors?.noLongerRequiredId &&
                      touched?.noLongerRequiredId && (
                        <CustomTypography
                          id="validate-subCancelReason"
                          color="error"
                          variant="body3"
                          className="pt-8"
                        >
                          {errors.noLongerRequiredId}
                        </CustomTypography>
                      )}
                  </Box>
                )}
                {isOthers && (
                  <Box className="mt-16">
                    <CustomTextField
                      id="other reason"
                      value={values.otherReason}
                      placeholder="Type Details"
                      size="small"
                      type="text"
                      className="mr-10"
                      onChange={(e) => {
                        setFieldValue('otherReason', e.target.value);
                      }}
                      multiLine={true}
                    />
                    {errors?.otherReason && touched?.otherReason && (
                      <CustomTypography
                        id="validate-otherCancelReason"
                        color="error"
                        variant="body3"
                        className="pt-8"
                      >
                        {errors.otherReason}
                      </CustomTypography>
                    )}
                  </Box>
                )}
              </CustomDialogContent>

              <CustomDialogActions>
                <Grid2 container>
                  <Grid2 className="mr-8">
                    <CustomButton
                      id="btnYes"
                      color="error"
                      variant="outlined"
                      onClick={() => {
                        onCloseCancelConsultPopup();
                        resetForm();
                      }}
                    >
                      Close
                    </CustomButton>
                  </Grid2>
                  <Grid2>
                    <CustomButton
                      id="btnConfirm-complete"
                      color="primary"
                      variant="contained"
                      onClick={() => handleSubmit()}
                    >
                      Submit
                    </CustomButton>
                  </Grid2>
                </Grid2>
              </CustomDialogActions>
            </CustomDialog>
          );
        }}
      </Formik>
    </Box>
  );
};

export default CancelConsultModel;
